import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { usd, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Briefcase, Plus, Pencil, Trash2, FileSignature, Mail, Eye, Printer, Send, Columns3 } from "lucide-react";
import { fmtDate } from "@/components/DatePicker";
import { toast } from "sonner";
import { JobDescriptionDialog } from "@/components/JobDescriptionDialog";
import { OfferLetterDialog } from "@/components/OfferLetterDialog";
import { Applicants } from "@/components/Applicants";
import { NewHires } from "@/components/NewHires";
import { OfferTemplateCard } from "@/components/OfferTemplateCard";
import { OfferCompare } from "@/components/OfferCompare";
import { DeclineSummary } from "@/components/DeclineSummary";

const OFFER_META = {
  draft: "bg-slate-100 text-slate-700 dark:bg-slate-500/15 dark:text-slate-300", sent: "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300",
  accepted: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300", declined: "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
};
export const OfferPill = ({ status }) => <span data-testid={`offer-status-${status}`} className={`inline-flex px-2 py-0.5 rounded-md text-xs font-semibold capitalize ${OFFER_META[status] || ""}`}>{status}</span>;

export default function Hiring() {
  const { user } = useAuth();
  const { activeCompanyId } = useCompany();
  const navigate = useNavigate();
  const companyId = user.role === "bureau_admin" ? activeCompanyId : user.company_id;
  const [jds, setJds] = useState([]);
  const [offers, setOffers] = useState([]);
  const [emailing, setEmailing] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [jdOpen, setJdOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [offerFor, setOfferFor] = useState(null);
  const [offerApplicant, setOfferApplicant] = useState(null);

  const load = useCallback(() => {
    if (!companyId) return;
    const params = user.role === "bureau_admin" ? { company_id: companyId } : {};
    api.get("/job-descriptions", { params }).then((r) => setJds(r.data)).catch(() => {});
    api.get("/offer-letters", { params }).then((r) => setOffers(r.data)).catch(() => {});
  }, [companyId, user.role]);
  const emailOffer = async (o) => {
    if (!o.candidate?.email) return toast.error("Add the candidate's email first (Edit Offer)");
    setEmailing(o.id);
    try { await api.post(`/offer-letters/${o.id}/send`); toast.success(`Offer emailed to ${o.candidate.email}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setEmailing(null); }
  };
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this job description?")) return;
    try { await api.delete(`/job-descriptions/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="hiring-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Hiring</h1>
          <p className="text-sm text-muted-foreground mt-1">Job descriptions and AI-generated offer letters with e-signature.</p>
        </div>
        <Button data-testid="new-jd-btn" onClick={() => { setEditing(null); setJdOpen(true); }}><Plus className="w-4 h-4 mr-1.5" /> New Job Description</Button>
      </div>

      <Tabs defaultValue={new URLSearchParams(window.location.search).get("tab") || "jobs"}>
        <TabsList><TabsTrigger value="jobs" data-testid="tab-jobs">Job Descriptions ({jds.length})</TabsTrigger><TabsTrigger value="offers" data-testid="tab-offers">Offer Letters ({offers.length})</TabsTrigger><TabsTrigger value="applicants" data-testid="tab-applicants">Applicants</TabsTrigger><TabsTrigger value="hires" data-testid="tab-hires">New Hires</TabsTrigger></TabsList>

        <TabsContent value="applicants" className="pt-4"><Applicants companyId={companyId} isAdmin={user.role === "bureau_admin"} jds={jds} onOffer={(a) => { const jd = jds.find((j) => j.id === a.job_id); if (!jd) return; setOfferApplicant(a); setOfferFor(jd); }} /></TabsContent>

        <TabsContent value="hires" className="pt-4 space-y-4"><NewHires companyId={companyId} isAdmin={user.role === "bureau_admin"} /></TabsContent>

        <TabsContent value="jobs" className="pt-4">
          {jds.length === 0 ? (
            <Card className="p-10 text-center text-muted-foreground"><Briefcase className="w-8 h-8 mx-auto mb-2 opacity-50" />No job descriptions yet. Create one manually or draft it with AI.</Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {jds.map((j) => (
                <Card key={j.id} data-testid={`jd-card-${j.id}`} className="p-5 flex flex-col">
                  <div className="flex items-start justify-between gap-2">
                    <div><div className="font-display font-semibold">{j.title}</div><div className="text-xs text-muted-foreground">{j.department} · {j.employment_type}{j.location && ` · ${j.location}`}</div></div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase ${j.status === "open" ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"}`}>{j.status}</span>
                  </div>
                  {(j.pay_min || j.pay_max) && <div className="mt-2 text-sm font-mono-num">{usd(j.pay_min)} – {usd(j.pay_max)}{j.pay_type === "hourly" ? " /hr" : " /yr"}</div>}
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-3 flex-1">{j.summary}</p>
                  <div className="mt-3 text-xs text-muted-foreground">{j.offer_count} offer letter{j.offer_count === 1 ? "" : "s"}</div>
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    <Button size="sm" variant="outline" data-testid={`jd-edit-${j.id}`} onClick={() => { setEditing(j); setJdOpen(true); }}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="outline" className="text-rose-600" data-testid={`jd-delete-${j.id}`} onClick={() => remove(j.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" data-testid={`jd-offer-${j.id}`} onClick={() => setOfferFor(j)}><FileSignature className="w-3.5 h-3.5 mr-1" /> Offer</Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="offers" className="pt-4 space-y-4">
          <OfferTemplateCard companyId={companyId} />
          <DeclineSummary companyId={companyId} isAdmin={user.role === "bureau_admin"} />
          {offers.length > 0 && <div className="flex justify-end"><Button variant="outline" size="sm" data-testid="offer-compare-btn" onClick={() => setCompareOpen(true)}><Columns3 className="w-4 h-4 mr-1.5" /> Compare offers</Button></div>}
          <OfferCompare open={compareOpen} onOpenChange={setCompareOpen} offers={offers} />
          <Card className="overflow-hidden">
            {offers.length === 0 ? <div className="p-10 text-center text-muted-foreground"><Mail className="w-8 h-8 mx-auto mb-2 opacity-50" />No offer letters yet. Open a job description and click "Offer".</div> : (
              <table className="w-full text-sm">
                <thead className="text-xs text-muted-foreground text-left bg-muted/50"><tr><th className="p-3">Candidate</th><th className="p-3">Position</th><th className="p-3">Start</th><th className="p-3">Compensation</th><th className="p-3">Status</th><th className="p-3">Updated</th><th className="p-3 text-right">Actions</th></tr></thead>
                <tbody>
                  {offers.map((o) => (
                    <tr key={o.id} data-testid={`offer-row-${o.id}`} className="border-t border-border hover:bg-accent/50 cursor-pointer" onClick={() => navigate(`/offers/${o.id}?mode=view`)}>
                      <td className="p-3"><div className="font-medium">{o.candidate?.name}</div><div className="text-xs text-muted-foreground">{o.candidate?.email}</div></td>
                      <td className="p-3">{o.job_title}</td>
                      <td className="p-3 font-mono-num text-xs">{fmtDate(o.start_date)}</td>
                      <td className="p-3 font-mono-num">{usd(o.salary)}{o.pay_type === "hourly" ? "/hr" : "/yr"}</td>
                      <td className="p-3"><OfferPill status={o.status} /></td>
                      <td className="p-3 text-xs text-muted-foreground font-mono-num">{fmtDate(o.updated_at?.slice(0, 10))}</td>
                      <td className="p-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex gap-1 justify-end flex-wrap">
                          <Button size="sm" variant="outline" className="h-7" data-testid={`offer-view-${o.id}`} onClick={() => navigate(`/offers/${o.id}?mode=view`)}><Eye className="w-3.5 h-3.5 mr-1" /> View</Button>
                          {o.status !== "signed" && <Button size="sm" variant="outline" className="h-7" data-testid={`offer-edit-${o.id}`} onClick={() => navigate(`/offers/${o.id}?mode=edit`)}><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>}
                          <Button size="sm" variant="outline" className="h-7" data-testid={`offer-print-${o.id}`} onClick={() => navigate(`/offers/${o.id}?mode=view&print=1`)}><Printer className="w-3.5 h-3.5 mr-1" /> Print</Button>
                          {o.status !== "signed" && <Button size="sm" className="h-7" data-testid={`offer-email-${o.id}`} disabled={emailing === o.id} onClick={() => emailOffer(o)}><Send className="w-3.5 h-3.5 mr-1" /> {emailing === o.id ? "Sending…" : "Email"}</Button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      <JobDescriptionDialog open={jdOpen} onOpenChange={setJdOpen} companyId={companyId} jd={editing} onSaved={load} />
      <OfferLetterDialog open={!!offerFor} onOpenChange={(o) => { if (!o) { setOfferFor(null); setOfferApplicant(null); } }} jd={offerFor} applicant={offerApplicant} />
    </div>
  );
}
